import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, ChevronDown } from "lucide-react";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ content }) {
  const cardRef = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [9, -9]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-11, 11]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = cardRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section id="top" className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16" data-testid="hero-section">
      <div className="absolute inset-0">
        <img src="/hero-bg.jpg" alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-storm/70 via-storm/80 to-storm" />
        <div className="absolute inset-0 hero-grid" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="absolute -top-32 -right-32 w-[36rem] h-[36rem] rounded-full bg-gold/10 blur-[140px] animate-pulse-glow" />
        <div className="absolute bottom-0 -left-40 w-[30rem] h-[30rem] rounded-full bg-gold-dark/10 blur-[120px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[1.15fr_1fr] gap-14 lg:gap-8 items-center w-full">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="inline-flex items-center gap-2 border border-gold/30 bg-gold/5 rounded-full px-4 py-1.5 mb-8"
            data-testid="hero-badge"
          >
            <ShieldCheck size={14} className="text-gold" />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">{content.hero_badge}</span>
          </motion.div>

          <h1 className="font-display font-bold italic uppercase leading-[0.92] tracking-tight text-5xl sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            <MaskedLine delay={0.25} className="text-white">{content.hero_line_1}</MaskedLine>
            <MaskedLine delay={0.4} className="gold-text">{content.hero_line_2}</MaskedLine>
            <MaskedLine delay={0.55} className="text-white">{content.hero_line_3}</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
            data-testid="hero-subtitle"
          >
            {content.hero_subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a href="#contact" data-testid="hero-quote-button"
              className="group inline-flex items-center gap-2 bg-gold text-storm font-bold px-7 py-3.5 rounded-full hover:bg-gold-light transition-colors">
              {content.cta_primary}
              <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a href={`tel:${content.phone_tel}`} data-testid="hero-call-button"
              className="inline-flex items-center gap-2 border border-gold/40 text-gold font-semibold px-7 py-3.5 rounded-full hover:bg-gold/10 transition-colors">
              <Phone size={16} />
              {content.cta_secondary} · {content.phone_display}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.25 }}
            className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-px bg-gold/10 border border-gold/10 rounded-xl overflow-hidden max-w-xl"
            data-testid="hero-stats"
          >
            {(content.stats || []).map((s, i) => (
              <div key={i} className="bg-storm/80 px-4 py-4" data-testid={`hero-stat-${i}`}>
                <div className="font-display font-bold text-2xl gold-text">{s.value}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative [perspective:1200px]"
        >
          <div className="absolute inset-0 -m-8 bg-gold/15 blur-[100px] rounded-full" />
          <motion.div
            ref={cardRef}
            onMouseMove={onMove}
            onMouseLeave={() => { mx.set(0.5); my.set(0.5); }}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="relative rounded-3xl border border-gold/25 bg-storm-800/50 backdrop-blur-md p-8 sm:p-12 shadow-[0_0_80px_rgba(229,193,88,0.12)]"
            data-testid="hero-logo-card"
          >
            <motion.img
              src="/logo.png"
              alt="DRC502 Construction LLC — Roofing, Siding, Gutters"
              className="w-full h-auto drop-shadow-[0_0_45px_rgba(229,193,88,0.35)]"
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              data-testid="hero-logo"
            />
            <div className="mt-6 flex items-center justify-center gap-3" style={{ transform: "translateZ(30px)" }}>
              <span className="h-px w-10 bg-gold/40" />
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold" data-testid="hero-tagline">{content.tagline}</span>
              <span className="h-px w-10 bg-gold/40" />
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.a
        href="#services"
        aria-label="Scroll to services"
        data-testid="hero-scroll-cue"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gold/60 hover:text-gold transition-colors"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <ChevronDown size={26} />
      </motion.a>
    </section>
  );
}
